import { Input, InputNumber, Form, AutoComplete, Select, Checkbox } from 'antd';
import { DEFAULT_CURRENCY, getCurrencySymbol } from '@/constants/currency';
import type { RateCard } from '@/models/rateCard';
import type { EditableCellProps } from './EditableCell.types';
import { toFormNumber } from '../../toRateCardFormValues';

const { Item } = Form;

const NUMERIC_FIELDS = new Set<keyof RateCard>([
  'monthlySpent',
  'buyRate',
  'sellRate',
  'benchmark',
  'costsFee'
]);

const getCellInitialValue = (dataIndex: keyof RateCard | undefined, record: Partial<RateCard>) => {
  if (!dataIndex) {
    return undefined;
  }
  if (dataIndex === 'currency') {
    return record.currency ?? DEFAULT_CURRENCY;
  }
  if (dataIndex === 'cost') {
    return record.cost ?? false;
  }
  if (dataIndex === 'kpis' || dataIndex === 'formats') {
    return record[dataIndex] ?? [];
  }
  if (NUMERIC_FIELDS.has(dataIndex)) {
    return toFormNumber(record[dataIndex]);
  }
  return record[dataIndex];
};

type MoneyInputProps = {
  recordCurrency?: string;
  value?: number;
  onChange?: (value: number | null) => void;
};

const MoneyInput: React.FC<MoneyInputProps> = ({ recordCurrency, value, onChange }) => {
  const formCurrency = Form.useWatch<string>('currency');
  const currency = formCurrency ?? recordCurrency ?? DEFAULT_CURRENCY;
  return (
    <InputNumber
      prefix={getCurrencySymbol(currency)}
      min={0}
      style={{ width: '100%' }}
      value={value}
      onChange={onChange}
    />
  );
};

const EditableCell: React.FC<EditableCellProps> = ({
  isEditing,
  dataIndex,
  type,
  record,
  rules,
  children,
  options,
  valuePropName,
  ...restProps
}) => {
  let inputNode;

  switch (type) {
    case 'text':
      inputNode = <Input />;
      break;
    case 'autocomplete':
      inputNode = <AutoComplete options={options} />;
      break;
    case 'select':
      inputNode = <Select options={options} />;
      break;
    case 'multiselect':
      inputNode = <Select mode="tags" options={options} />;
      break;
    case 'pound':
    case 'money':
      inputNode = <MoneyInput recordCurrency={record?.currency} />;
      break;
    case 'percent':
      inputNode = <InputNumber prefix="%" min={0} max={100} />;
      break;
    case 'checkbox':
      inputNode = <Checkbox />;
      break;
    default:
      inputNode = <Input />;
  }

  return (
    <td {...restProps}>
      {isEditing ? (
        <Item
          help={false}
          name={dataIndex}
          initialValue={getCellInitialValue(dataIndex, record)}
          rules={rules}
          style={{ margin: 0 }}
          valuePropName={valuePropName}
        >
          {inputNode}
        </Item>
      ) : (
        children
      )}
    </td>
  );
};

export default EditableCell;
